// import external libraries
import { useQuery } from 'react-query';
import dayjs from 'dayjs';

// import hooks
import { queries, requests } from 'RestAPIs/rapini';

// import utils
import { entityFiltersForView, toCategoryFilter } from './Backlog.utils';
import { costCenterIdsOf } from './components/BacklogFilters/BacklogFilters.utils';
import { extractPlanningEntity } from './components/BacklogGrid/BacklogGrid.utils';

// import constants
import { BACKLOG_ACTIVITY_SCHEDULING_LABEL } from './Backlog.consts';

// import types
import type { QueryClient } from 'react-query';
import type {
  BackLogResponse,
  BackLogResponseList,
  PlanningEntityType,
  SchedulingLabel
} from 'generated/index';
import type {
  BacklogEntityFilter,
  BacklogTaskCategory,
  FilterOption
} from './Backlog.types';
import type { BacklogPlanUnitOption } from './components/BacklogFilters/components/BacklogPlanUnitFilter/BacklogPlanUnitFilter.types';

type UseGetBacklogParams = {
  startDate: Date;
  endDate: Date;
  includeAllSummaries: boolean;
  entityFilters: BacklogEntityFilter[];
  activityIds: string[];
  /** The tiles the planner has on — the backend detects only these. */
  activeCategories: BacklogTaskCategory[];
  /** Which planning entity's rows to report on — one type at a time. */
  rowEntityType: PlanningEntityType;
  /** The picked plan units — the employee view's scope, sent as the cost centres they stand for. */
  planUnits: BacklogPlanUnitOption[];
  /**
   * The picked employees — the employee view's own narrowing within its plan unit. Sent by the numeric id
   * the picker carries: that list is the plan unit's cost-centre employee list, which names a person by
   * `id` and holds no planning-entity uuid at all.
   */
  employees: FilterOption[];
  /**
   * True while the page has nothing worth asking yet: a view still missing the filter it cannot work
   * without (see missingRequiredFilter), or a remembered activity filter whose activities have not come in
   * to be resolved against. Decided by the page rather than here, so what it shows and what it withholds
   * can never disagree.
   */
  isRequestHeld: boolean;
};

/** The root of every backlog query key, and what a targeted refresh matches its cached queries by. */
const BACKLOG_QUERY_KEY = 'backlog';

/**
 * Everything a backlog request is scoped by — and, as `[BACKLOG_QUERY_KEY, scope]`, the query key itself.
 *
 * One named object rather than a flat list of key elements on purpose: `refreshBacklogForEntities` has to
 * read a cached query's scope back to repeat its request, and with a positional key every field added here
 * has to be mirrored in that destructure — miss one (as `planUnitCostCenterIds` was) and every field after
 * it silently shifts by one, so the row type is read out of the category slot. Read by name, that cannot
 * happen.
 *
 * The list fields are sorted so the key only changes when the filter *contents* change, not on every array
 * identity — react-query hashes the object with a stable, key-sorted stringify.
 */
type BacklogQueryScope = {
  start: string;
  end: string;
  includeAllSummaries: boolean;
  /** The view's own entity picks as `{type}:{id}`, sorted — see entityFiltersForView. */
  entityKey: string;
  /** The picked plan units as the cost centres they stand for, sorted. */
  planUnitCostCenterIds: number[];
  /** The picked employees as the numeric ids their own list names them by, sorted. */
  pickedEmployeeIds: number[];
  /** The applied activity ids, sorted. */
  activityIds: string[];
  /**
   * The tiles that were on, sorted. Part of the scope, or switching a tile would be served the response
   * fetched for the previous selection — rows for a category that is now off.
   */
  categories: BacklogTaskCategory[];
  /** The planning entity type whose rows the query reports on — one type at a time. */
  rowEntityType: PlanningEntityType;
};

/**
 * The plan units of a scope as request entities. A plan unit is a cost centre, so it travels by its numeric
 * `id` rather than an `entityId`, and it is what scopes the employee view server-side — a request that
 * leaves it out resolves to no employees at all (see `BacklogDelegate.employeesInScope`), which is why a
 * targeted refresh has to repeat it rather than send the affected entities alone.
 */
const planUnitEntitiesOf = (scope: BacklogQueryScope) =>
  scope.planUnitCostCenterIds.map((id) => ({
    id,
    planningEntityType: 'ORGANIZATIONAL_UNIT_TO_PLAN' as const
  }));

/**
 * The picked employees of a scope as request entities. Like a plan unit, an employee picked in this view
 * travels by its numeric `id` — the cost-centre employee list the picker reads carries no entity uuid — and
 * the backend narrows the plan unit's employees by it (see `BacklogDelegate.employeesInScope`, which
 * matches an employee on either id and applies each narrowing only when it is there).
 *
 * A targeted refresh repeats these alongside the entities it is refreshing, so it stays inside the filter
 * the planner set instead of splicing in a row for an employee they filtered out.
 */
const pickedEmployeeEntitiesOf = (scope: BacklogQueryScope) =>
  scope.pickedEmployeeIds.map((id) => ({
    id,
    planningEntityType: 'EMPLOYEE' as const
  }));

/**
 * Reads the backlog timeline for every care receiver that has a prescription in the period.
 *
 * `getBacklogTimeline` is a POST — rapini therefore generates it as a mutation — but it is
 * semantically a read, so it is wrapped in `useQuery` here.
 *
 * No `planningEntities` are sent when nothing is picked: the backend resolves the
 * care receivers itself, in one query, narrowed to those that actually have a prescription
 * overlapping the period. When filters are set, they narrow that same resolution.
 *
 * `includeAllSummaries: false` asks the backend for deviating weeks only — shortages and surpluses
 * alike — and it then also leaves out rules and care receivers that have nothing to report.
 */
export const useGetBacklog = ({
  startDate,
  endDate,
  includeAllSummaries,
  entityFilters,
  planUnits,
  employees,
  activityIds,
  activeCategories,
  rowEntityType,
  isRequestHeld
}: UseGetBacklogParams) => {
  const start = dayjs(startDate).format('YYYY-MM-DD');
  const end = dayjs(endDate).format('YYYY-MM-DD');

  // Only the active view's picks narrow the list — see entityFiltersForView.
  const viewEntityFilters = entityFiltersForView(entityFilters, rowEntityType);

  // A plan unit is a cost centre, and it scopes the employee view server-side: the request reports on that
  // plan unit's employees. It belongs on the request beside the entity picks rather than only gating them
  // in the browser, which is all it did while the backlog had no field for it.
  const planUnitCostCenterIds =
    rowEntityType === 'EMPLOYEE' ? costCenterIdsOf(planUnits) : [];
  // The employee picks narrow that same view and nothing else, so they are read under the same condition:
  // sending them anywhere else would narrow by an entity type the request does not report rows for.
  // A positive whole number or nothing: these picks are remembered across visits, so one left behind in
  // an older shape must drop out rather than narrow the request to an employee id that cannot exist —
  // which the backend would answer with an empty view and no reason given.
  const pickedEmployeeIds =
    rowEntityType === 'EMPLOYEE'
      ? employees
          .map(({ id }) => Number(id))
          .filter((id) => Number.isInteger(id) && id > 0)
      : [];

  // Sorted, so the scope — and with it the query key — only changes when the filter contents actually
  // change, not on every array identity.
  const scope: BacklogQueryScope = {
    start,
    end,
    includeAllSummaries,
    entityKey: viewEntityFilters
      .map((f) => `${f.planningEntityType}:${f.entityId}`)
      .sort()
      .join(','),
    planUnitCostCenterIds: [...planUnitCostCenterIds].sort((a, b) => a - b),
    pickedEmployeeIds: [...pickedEmployeeIds].sort((a, b) => a - b),
    activityIds: [...activityIds].sort(),
    categories: [...activeCategories].sort(),
    rowEntityType
  };

  // No onError snackbar: Backlog.tsx already renders an inline error state with a retry button for
  // isError, which is discoverable and actionable where a snackbar just disappears.
  return useQuery<BackLogResponseList>(
    [BACKLOG_QUERY_KEY, scope],
    () =>
      requests.getBacklogTimeline({
        period: { start, end },
        includeAllSummaries,
        planningEntities: [
          ...viewEntityFilters.map((f) => ({
            entityId: f.entityId,
            planningEntityType: f.planningEntityType
          })),
          ...planUnitEntitiesOf(scope),
          ...pickedEmployeeEntitiesOf(scope)
        ],
        activityIds,
        categories: toCategoryFilter(activeCategories),
        planningEntityType: rowEntityType
      }),
    {
      refetchOnWindowFocus: false,
      // Nothing selected means nothing to show, so there is nothing worth asking for — and a view that is
      // not scoped yet has not been asked a question worth sending.
      enabled: activeCategories.length > 0 && !isRequestHeld
    }
  );
};

/** The activity list, with the loading flag the page needs to tell "no activities" from "not in yet". */
type BacklogActivityOptions = {
  options: FilterOption[];
  isLoading: boolean;
};

/**
 * Every activity the tenant plans with, as filter options.
 *
 * Read from the activities endpoint rather than from the takenlijst's own response, which is what the filter
 * used to be built from. That cost a second, deliberately unnarrowed backlog fetch — without it the options
 * collapsed to the activity already picked, and a planner could not add a second one. The activities are
 * master data, so asking for them directly is both lighter on the backend and a complete list: an activity
 * nobody has a task for is now offered too, and picking it simply reports nothing.
 *
 * `MRP` is not decoration: with no scheduling label the endpoint answers with an empty list, and MRP is the
 * label that returns the planning activities the takenlijst's prescriptions are written in.
 */
export const useBacklogActivityOptions = (): BacklogActivityOptions => {
  // Without this the mapping below hands back a new array every render, and every useMemo downstream of
  // activityOptions — the selected options, the row filter, the built rows — recomputes with it.
  'use memo';

  const { data = [], isLoading } = queries.useGetActivities(
    // The endpoint takes a comma-separated string of labels where the generated client types an array,
    // hence the cast — the same shape the entity search and the appointment form send.
    BACKLOG_ACTIVITY_SCHEDULING_LABEL as unknown as SchedulingLabel[]
  );

  return {
    options: data.map(({ entityId, code, name }) => ({
      id: entityId,
      // "{name} - {code}", the way the filter has always named an activity.
      name: `${name} - ${code}`
    })),
    isLoading
  };
};

/** A backlog row's entity id, derived the same way the grid rows are keyed. */
const backlogEntityId = (row: BackLogResponse): string =>
  extractPlanningEntity(
    row.planningEntity,
    (row.planningEntityType ?? 'CLIENT') as PlanningEntityType
  ).id;

/**
 * Merge a targeted backlog result into the cached list: refresh only the affected entities and keep every
 * other cached row exactly as the full backlog returned it.
 *
 * The fetch is trusted **only for the affected entities**. The backend scopes a targeted request to the
 * requested entities of the requested row type (see `BacklogEntityRowsHelper.reportOnRows`, which drops a
 * finding on an entity outside the request's scope), so it should return just those — but taking only the
 * affected entities out of the result is kept as a safety net:
 * were an unscoped result to also carry an incomplete, overlap-only row for some other overlapping entity,
 * splicing it in would duplicate that entity or overwrite its full cached row with a stripped-down one. So
 * the affected entities are replaced with their freshly fetched rows and all other cached rows are left
 * untouched. An affected entity the fetch no longer returns (nothing left to report — e.g. an unassigned
 * care receiver whose overlap is gone) is dropped.
 */
export const mergeBacklogEntities = (
  cached: BackLogResponseList,
  fetched: BackLogResponseList,
  affectedEntityIds: string[]
): BackLogResponseList => {
  const affected = new Set(affectedEntityIds);
  const refreshed = fetched.filter((row) => affected.has(backlogEntityId(row)));
  const kept = cached.filter((row) => !affected.has(backlogEntityId(row)));
  return [...kept, ...refreshed];
};

/**
 * Refresh the backlog for only the given entities instead of invalidating the whole list (which refetches
 * every care receiver). Fetches those entities' rows and merges them into each *mounted* backlog query. A
 * delete passes the appointment's own participants; an update passes the entities the update reports as
 * affected. No entities → nothing to do.
 *
 * The caller hands over a mixed bag — the appointment's care receivers, employees, resources and its
 * workplace — because it cannot know which list is on screen. Each cached query narrows that bag itself:
 * the ids are sent as entities of *that* query's row type, so a query showing employee rows asks about the
 * employees and simply matches nothing for the rest.
 */
export const refreshBacklogForEntities = async (
  queryClient: QueryClient,
  entityIds: string[]
): Promise<void> => {
  const uniqueIds = Array.from(new Set(entityIds.filter(Boolean)));
  if (!uniqueIds.length) {
    return;
  }

  // Mounted queries only: getQueriesData also hands back inactive cache entries from periods the planner
  // navigated away from, and each of those would cost a full backlog request of its own while nobody is
  // looking at its result.
  const cachedQueries = queryClient.getQueriesData<BackLogResponseList>({
    queryKey: BACKLOG_QUERY_KEY,
    active: true
  });

  await Promise.all(
    cachedQueries.map(async ([queryKey, cached]) => {
      if (!cached) {
        return;
      }
      const [, scope] = queryKey as [string, BacklogQueryScope];

      // A narrowed view must stay narrowed: refreshing an entity the planner filtered out would splice a
      // row into the list that the filter says does not belong there. The scope carries the view's picks
      // as `{type}:{id}`, so the affected entities are intersected with them.
      const viewEntityIds = scope.entityKey
        ? scope.entityKey.split(',').map((pick) => pick.split(':')[1])
        : null;
      const affectedIds = viewEntityIds
        ? uniqueIds.filter((id) => viewEntityIds.includes(id))
        : uniqueIds;
      if (!affectedIds.length) {
        return;
      }

      const fetched = await requests.getBacklogTimeline({
        period: { start: scope.start, end: scope.end },
        includeAllSummaries: scope.includeAllSummaries,
        planningEntities: [
          // Typed as this query's own row type: that is the only type it reports rows for, so it is the
          // only type these ids can narrow.
          ...affectedIds.map((entityId) => ({
            entityId,
            planningEntityType: scope.rowEntityType
          })),
          // The plan units too, and not only the affected entities: they are what scopes the employee
          // view server-side, so a refresh that dropped them would resolve to no employees at all — and
          // the merge below would then delete the very rows it set out to refresh.
          ...planUnitEntitiesOf(scope),
          // And the employee picks, which narrow that scope: without them the refresh would report on an
          // employee the planner filtered out and the merge would splice that row in.
          ...pickedEmployeeEntitiesOf(scope)
        ],
        // The rest of the request is what this cached query was fetched with, so the rows that come back
        // are comparable to the ones being replaced.
        activityIds: scope.activityIds,
        categories: toCategoryFilter(scope.categories),
        planningEntityType: scope.rowEntityType
      });
      queryClient.setQueryData<BackLogResponseList>(
        queryKey,
        mergeBacklogEntities(cached, fetched, affectedIds)
      );
    })
  );
};
